import { MappingSystem } from '@atlas/types'
import {
	SectionCard,
	SelectField,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useCreateMapping, useMappings, useValuesByAxisCode, useValueWithAxis } from '@/hooks/useClassification'
import type { AxisValueDTO } from '@/services/classification.service'
import { TextField } from './FormField'

/**
 * Per-system mapping grid for a value: rows = geography scope (worldwide
 * default + per-geo overrides), columns = external systems.
 * Empty cells where no mapping exists yet — the whole external-code surface is
 * inspectable even when sparse.
 */
/**
 * The systems a value can be mapped to, from the shared enum rather than literals, so a
 * renamed or added system reaches this grid without a second list to remember.
 *
 * Title first: it is the name a human reads, and the only system whose rows resolve as an
 * object's title. The rest are integration identifiers.
 */
const SYSTEMS = [MappingSystem.TAXONOMY_TITLE, MappingSystem.VEEVA, MappingSystem.WORKFRONT] as const

/** `taxonomy_title` → `Taxonomy title`; the raw code reads as a database value in a header. */
const systemLabel = (system: string): string => system.replace(/_/g, ' ')
type MappingDTO = { system: string; geoValueId: string | null; externalCode: string }

const WORLDWIDE = '__default__'
type GeoOption = { value: string; label: string }

/** Build the geo-scope dropdown options: Worldwide default + geography values,
 *  indented by hierarchy depth. The registry serves values parent-before-child,
 *  so the indentation lines up with the order they arrive in. */
function buildGeoOptions(geoValues: AxisValueDTO[]): GeoOption[] {
	const byId = new Map(geoValues.map((v) => [v.valueId, v]))
	const depth = (v: AxisValueDTO): number => {
		let d = 0
		let cur: AxisValueDTO | undefined = v
		while (cur?.parentId && byId.has(cur.parentId)) {
			d++
			cur = byId.get(cur.parentId)
		}
		return d
	}
	const opts = geoValues.map((v) => ({ value: v.valueId, label: `${'  '.repeat(depth(v))}${v.label}` }))
	return [{ value: WORLDWIDE, label: 'Worldwide default' }, ...opts]
}

/** Human label for a geo scope id (or "Worldwide default"). */
function geoLabel(geoValueId: string | null, geoValues: AxisValueDTO[]): string {
	if (geoValueId === null) return 'Worldwide default'
	return geoValues.find((v) => v.valueId === geoValueId)?.label ?? `geo #${geoValueId}`
}

function Grid({ mappings, geoValues }: { mappings: MappingDTO[]; geoValues: AxisValueDTO[] }) {
	const scopes = Array.from(new Set(mappings.map((m) => m.geoValueId ?? WORLDWIDE)))
	if (!scopes.includes(WORLDWIDE)) scopes.unshift(WORLDWIDE)
	// A Workfront cell can hold several labels (an alias beside the name), so it lists them all.
	const cell = (scope: string, system: string) => {
		const geo = scope === WORLDWIDE ? null : scope
		const codes = mappings
			.filter((m) => m.system === system && (m.geoValueId ?? null) === geo)
			.map((m) => m.externalCode)
		return codes.length > 0 ? codes.join(', ') : undefined
	}
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Geo scope</TableHead>
					{SYSTEMS.map((s) => (
						<TableHead key={s} className="capitalize">
							{systemLabel(s)}
						</TableHead>
					))}
				</TableRow>
			</TableHeader>
			<TableBody>
				{scopes.map((scope) => (
					<TableRow key={scope}>
						<TableCell className="font-medium">{geoLabel(scope === WORLDWIDE ? null : scope, geoValues)}</TableCell>
						{SYSTEMS.map((s) => {
							const val = cell(scope, s)
							return (
								<TableCell key={s} className={val ? '' : 'text-ds-text-subtle'}>
									{val ?? '—'}
								</TableCell>
							)
						})}
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}

function AddMappingForm({ valueId, geoOptions }: { valueId: string; geoOptions: GeoOption[] }) {
	const createMapping = useCreateMapping(valueId)
	const blank = { system: SYSTEMS[0], geoValueId: WORLDWIDE, externalCode: '' }
	const [form, setForm] = useState<{ system: string; geoValueId: string; externalCode: string }>(blank)
	const submit = () => {
		if (!form.externalCode) return
		createMapping.mutate(
			{
				system: form.system,
				geoValueId: form.geoValueId === WORLDWIDE ? null : form.geoValueId,
				externalCode: form.externalCode,
			},
			{ onSuccess: () => setForm(blank) },
		)
	}
	const set = (k: 'system' | 'geoValueId' | 'externalCode') => (v: string) => setForm({ ...form, [k]: v })
	return (
		<SectionCard title="Add / update mapping">
			<div className="flex flex-wrap gap-3 items-end">
				{/* Both always hold a value (system defaults to the first, geo scope to
				    worldwide), so neither offers an "unset" option. */}
				<SelectField
					label="System"
					value={form.system}
					onChange={set('system')}
					options={SYSTEMS.map((s) => ({ value: s, label: systemLabel(s) }))}
					isClearable={false}
				/>
				<SelectField
					label="Geo scope"
					value={form.geoValueId}
					onChange={set('geoValueId')}
					options={geoOptions}
					isClearable={false}
				/>
				<TextField
					label="External code"
					value={form.externalCode}
					onChange={set('externalCode')}
					placeholder="Bravecto (Injectable)"
				/>
				<Button onPress={submit} isDisabled={createMapping.isPending}>
					{createMapping.isPending ? 'Saving…' : 'Save'}
				</Button>
			</div>
		</SectionCard>
	)
}

export function MappingGridPage() {
	const { valueId = '' } = useParams()
	const { data: mappings = [], isLoading } = useMappings(valueId)
	const { data: geoValues = [] } = useValuesByAxisCode('geography')
	const geoOptions = buildGeoOptions(geoValues)
	// Names the value being mapped — the page is reached by id, so without this it reads as
	// "Value mappings" with no indication of whose.
	const subject = useValueWithAxis(valueId)
	return (
		<div className="space-y-6">
			<div>
				<Link to="/admin/classification" className="text-sm text-ds-primary hover:underline">
					← Axis registry
				</Link>
				<h1 className="text-2xl font-semibold mt-1">{subject ? subject.value.label : `Value #${valueId}`}</h1>
				<p className="text-ds-text-muted">
					Value mappings{subject?.axis ? ` · ${subject.axis.name}` : ''}
					{subject ? ` · ${subject.value.code}` : ''} —{' '}
					{isLoading ? 'Loading…' : `${mappings.length} mappings across ${SYSTEMS.length} systems`}
				</p>
			</div>
			<SectionCard
				title="Mapping grid"
				description="External code per system. Rows are geo scopes — “Worldwide default” plus any per-market overrides."
			>
				<Grid mappings={mappings} geoValues={geoValues} />
			</SectionCard>
			<AddMappingForm valueId={valueId} geoOptions={geoOptions} />
		</div>
	)
}
